import {weakMemo} from '@packages/utilities/memo';
import {emptyObject} from '@packages/utilities/fp/utils';
import camelCase from 'lodash/camelCase';
import capitalize from 'lodash/capitalize';
import * as quickEffectsActionLists from './quick-effects-action-lists';
import type {EventType} from '@packages/systems/ix2/types-core';
import {QuickEffectIds} from '@packages/systems/ix2/shared-constants';

const QuickEffectsIdList = Object.keys(QuickEffectIds);

export const isQuickEffect = (actionTypeId?: string | null) => {
  // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
  return QuickEffectsIdList.includes(actionTypeId);
};

const normalizeEffectName = (effect: string) =>
  camelCase(effect.replace(/_EFFECT$/, ''));

const normalizeDirection = (direction: string) =>
  direction.split('_').map(capitalize).join('');

export const formatEventForQuickEvent = weakMemo((event: any) => {
  const actionTypeId = event?.action?.actionTypeId;

  if (isQuickEffect(actionTypeId)) {
    const {direction, effectIn, loop, playInReverse} =
      event?.config ?? emptyObject;

    const quickEffectActionListName = buildQuickEffectActionListId(
      actionTypeId,
      effectIn,
      direction,
      loop,
      playInReverse
    );

    return {
      ...event,
      action: {
        ...event.action,
        config: {
          ...event.action.config,
          actionListId: quickEffectActionListName,
        },
      },
    };
  }

  return event;
});

const buildQuickEffectActionListId = (
  effectType: string,
  effectIn: null | boolean,
  direction: null | string,
  loop: boolean,
  playInReverse: boolean
) => {
  const name = normalizeEffectName(effectType);
  let effectInOut = '';

  if (effectIn != null) {
    effectInOut = effectIn ? 'In' : 'Out';
  }

  let baseName = `${name}${effectInOut}`;

  if (direction) {
    return `${baseName}${normalizeDirection(direction)}`;
  }

  if (loop) {
    baseName += 'Loop';
  }

  if (playInReverse) {
    baseName += 'Reverse';
  }

  return baseName;
};

type QuickEffectActionListIds = keyof typeof quickEffectsActionLists;
type QuickEffectActionList =
  (typeof quickEffectsActionLists)[keyof typeof quickEffectsActionLists];
type GetQuickActionListsReturn = Partial<
  Record<QuickEffectActionListIds, QuickEffectActionList>
>;

export const getQuickActionLists = weakMemo(
  (quickEffectEvents: Array<any>): GetQuickActionListsReturn =>
    quickEffectEvents.reduce<Record<string, any>>((result, event) => {
      const actionListId = event?.action?.config?.actionListId;
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'typeof import("/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/packages/systems/ix2/shared-utils/quick-effects-action-lists")'.
      const quickEffectActionList = quickEffectsActionLists[actionListId];

      if (quickEffectActionList) {
        result[actionListId] = quickEffectActionList;
      }

      return result;
    }, {})
);

export const getQuickEffectId = (event: any) => {
  const effectType = event.action?.actionTypeId;
  const {direction, effectIn, loop, playInReverse} = event.config ?? {};
  return buildQuickEffectActionListId(
    effectType,
    effectIn,
    direction,
    loop,
    playInReverse
  );
};

/**
 * ## `isPresetEvent`
 *
 * Returns whether an IX is a preset (quick effect) event, supporting both
 * legacy and new event data structure
 */
export function isPresetEvent(event: EventType): boolean {
  // Handles new IX2 data structure where a quick effect is now
  // determined via animationType, instead of actionListId
  if (
    event?.animationType === 'preset' &&
    isQuickEffect(event?.action?.actionTypeId)
  ) {
    return true;
  }

  // Handles legacy data structure where a quick effect was set on
  // both actionTypeId and actionListId to prevent legacy sites from
  // breaking
  if (isQuickEffect(event?.action?.config?.actionListId)) {
    return true;
  }

  return false;
}

export const forTestSuite = {
  buildQuickEffectActionListId,
} as const;
